import { useCallback, useMemo, useState } from 'react';

import { useApolloClient, useMutation, useQuery } from '@apollo/client/react';

import { GET_DOCUMENT_TYPES } from '@modules/_shared/components/FileTypeAutocomplete';
import { isMimetypeVideo } from '@modules/_shared/components/PropertyFiles';
import {
  uploadFileAsync,
  useFileUpload,
} from '@modules/_shared/utils/files/file-upload';
import {
  GET_PROPERTY_IMAGES,
  GET_PROPERTY_IMAGES_COUNT,
  INSERT_PROPERTY_IMAGES_BATCH,
} from '@modules/listings/ListingPropertyImages/listingPropertyQueries';
import {
  CREATE_PROPERTY_FILE,
  GET_LISTING_DETAILS,
  LISTING_DOCUMENTS,
} from '@modules/listings/lotsQueries';

export type MediaUploadResult = {
  images: number;
  videos: number;
  documents: number;
  failed: number;
};

const isImageFile = (file: File): boolean => file.type.startsWith('image/');

export const useListingMediaUpload = ({
  propertyId,
}: {
  propertyId?: string | null;
}) => {
  const client = useApolloClient();
  const [uploadImage] = useFileUpload('image');
  const [uploadFile] = useFileUpload('file');
  const [insertImagesBatch] = useMutation(INSERT_PROPERTY_IMAGES_BATCH);
  const [createPropertyFile] = useMutation(CREATE_PROPERTY_FILE);
  const [isUploading, setIsUploading] = useState(false);

  const { data: docTypesData } = useQuery(GET_DOCUMENT_TYPES, {
    variables: { types: ['property'] },
  });

  const videoDocumentTypeId = useMemo(
    () =>
      (docTypesData?.document_types ?? []).find(type => type.name === 'video')
        ?.id,
    [docTypesData?.document_types],
  );

  const uploadFiles = useCallback(
    async (files: File[]): Promise<MediaUploadResult> => {
      const result: MediaUploadResult = {
        images: 0,
        videos: 0,
        documents: 0,
        failed: 0,
      };

      if (propertyId == null || files.length === 0) {
        return result;
      }

      const imageFiles = files.filter(isImageFile);
      const otherFiles = files.filter(file => !isImageFile(file));

      setIsUploading(true);
      try {
        if (imageFiles.length > 0) {
          const uploaded = await Promise.allSettled(
            imageFiles.map(file => uploadFileAsync(uploadImage, file)),
          );
          const succeeded = uploaded
            .filter(
              (r): r is PromiseFulfilledResult<{ id: string; url: string }> =>
                r.status === 'fulfilled',
            )
            .map(r => r.value);
          result.failed += uploaded.length - succeeded.length;

          if (succeeded.length > 0) {
            try {
              await insertImagesBatch({
                variables: {
                  objects: succeeded.map(file => ({
                    property_id: propertyId,
                    image: { data: { url: file.url, id: file.id } },
                  })),
                },
              });
              result.images += succeeded.length;
            } catch {
              result.failed += succeeded.length;
            }
          }
        }

        let resolvedVideoTypeId = videoDocumentTypeId;
        if (
          resolvedVideoTypeId == null &&
          otherFiles.some(file => isMimetypeVideo(file.type))
        ) {
          try {
            const { data } = await client.query({
              query: GET_DOCUMENT_TYPES,
              variables: { types: ['property'] },
            });
            resolvedVideoTypeId = (data?.document_types ?? []).find(
              type => type.name === 'video',
            )?.id;
          } catch {
            resolvedVideoTypeId = undefined;
          }
        }

        for (const file of otherFiles) {
          try {
            const data = await uploadFileAsync(uploadFile, file);
            const isVideo = isMimetypeVideo(file.type);

            await createPropertyFile({
              variables: {
                object: {
                  property_id: propertyId,
                  file: {
                    data: {
                      name: file.name,
                      url: data.url,
                      size: file.size,
                      mimetype: file.type,
                      ...(isVideo && resolvedVideoTypeId != null
                        ? { document_type_id: resolvedVideoTypeId }
                        : {}),
                    },
                  },
                },
              },
            });

            if (isVideo) {
              result.videos += 1;
            } else {
              result.documents += 1;
            }
          } catch {
            result.failed += 1;
          }
        }

        await client.refetchQueries({
          include: [
            LISTING_DOCUMENTS,
            GET_LISTING_DETAILS,
            GET_PROPERTY_IMAGES_COUNT,
            ...(result.images > 0 ? [GET_PROPERTY_IMAGES] : []),
          ],
        });
      } finally {
        setIsUploading(false);
      }

      return result;
    },
    [
      propertyId,
      uploadImage,
      uploadFile,
      insertImagesBatch,
      createPropertyFile,
      videoDocumentTypeId,
      client,
    ],
  );

  return { uploadFiles, isUploading };
};
